import type { AuthResponse } from './types';

/**
 * Where the API lives.
 *
 * Empty on the web, and deliberately so: the API serves this page, every request is same-origin and
 * the refresh cookie needs no relaxing to reach it. A native shell is the case that needs a value —
 * a Capacitor WebView loads its assets from `capacitor://localhost`, so a relative `/api/...`
 * resolves against the local bundle and finds nothing at all. Set `VITE_API_BASE_URL` when building
 * for that target.
 *
 * Read through a cast rather than by declaring it on `ImportMetaEnv`. Augmenting that interface
 * merges with the one Vite ships, and a modifier that does not match theirs — `readonly` against
 * their mutable — is a compile error in a build this repository cannot run without a registry. A
 * cast is honest about what is known and cannot break either configuration.
 */
const BASE = (
  ((import.meta as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL ?? '')
).replace(/\/$/, '');

/** Prefixes an absolute path with the API base; leaves an already-absolute URL alone. */
function url(path: string): string {
  return path.startsWith('/') ? `${BASE}${path}` : path;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The access token lives in memory only.
 *
 * localStorage would survive a refresh, which is convenient, but it is also readable by any script
 * on the page — so a single XSS bug would hand over a working session. The refresh token is in an
 * httpOnly cookie the server sets, so a reload silently re-authenticates instead. The convenience is
 * kept; the exposure is not.
 */
let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

/**
 * A single in-flight refresh, shared by every request that discovers an expired token.
 *
 * Without this, a page that fires six queries at once on load would attempt six refreshes. Five of
 * them lose the rotation race, and the server treats a losing replay as suspicious — so the user
 * gets signed out for the crime of opening a page.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(url('/api/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });

      if (!response.ok) {
        return false;
      }

      const auth = (await response.json()) as AuthResponse;
      accessToken = auth.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so callers awaiting this promise all observe the same result before
      // a subsequent failure can start a second attempt.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Set internally to stop a failed refresh from retrying forever. */
  isRetry?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, isRetry, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);

  if (accessToken) {
    requestHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  let payload: BodyInit | undefined;

  if (body instanceof FormData) {
    // Deliberately not setting Content-Type: the browser has to add the multipart boundary, and
    // setting it by hand produces a request the server cannot parse.
    payload = body;
  } else if (body !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
    payload = JSON.stringify(body);
  }

  const response = await fetch(url(path), {
    ...rest,
    headers: requestHeaders,
    body: payload,
    credentials: 'include',
  });

  if (response.status === 401 && !isRetry) {
    if (await refreshAccessToken()) {
      return api<T>(path, { ...options, isRetry: true });
    }

    accessToken = null;
    onUnauthenticated?.();
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  let message = 'Došlo je do greške. Pokušajte ponovo.';
  let fieldErrors: Record<string, string[]> | undefined;

  try {
    const problem = (await response.json()) as {
      title?: string;
      detail?: string;
      error?: string;
      errors?: Record<string, string[]>;
    };

    message = problem.title ?? problem.error ?? problem.detail ?? message;
    fieldErrors = problem.errors;
  } catch {
    /* A non-JSON error body is still an error; the default message covers it. */
  }

  return new ApiError(response.status, message, fieldErrors);
}

/**
 * Builds a query string, dropping empties and expanding arrays into repeated keys.
 *
 * Repeated keys rather than a comma-joined value because that is what the server's model binder
 * reads natively, and because it keeps the URL honest: every filter is visible as its own parameter
 * in the address bar, so a filtered search can be read, edited and shared.
 */
export function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== '') {
          search.append(key, String(item));
        }
      }
    } else {
      search.append(key, String(value));
    }
  }

  return search.toString();
}
